const FEATURE_DEMAND_PREFIX = "feature-"
const DEFAULT_MODE = "merge"

export function parseFeaturePackIds(pack) {
    const { items, onlyFeatureDemands } = getPackItems(pack)
    return uniqueItems(
        items
            .map(getPackItemId)
            .filter((id) => isFeatureId(id, onlyFeatureDemands))
            .map(stripFeaturePrefix)
            .filter(Boolean)
    )
}

export function featureValueFromId(id, features = []) {
    const feature = features.find((item) => item?.id === id)
    if (feature?.name) return `${feature.name} [${id}]`
    return `feature-${id} [${id}]`
}

export function featureIdFromValue(value) {
    if (!value) return ""
    if (typeof value !== "string") return value?.id ?? ""
    const bracketed = value.match(/\[([^\]]+)\]$/)
    if (bracketed) return stripFeaturePrefix(bracketed[1])
    return stripFeaturePrefix(value)
}

export function featureDemandFromValue(value) {
    const id = featureIdFromValue(value)
    if (!id) return ""
    return `${FEATURE_DEMAND_PREFIX}${id}`
}

export function buildFeaturePackDiff({ current = [], packIds = [], mode = DEFAULT_MODE } = {}) {
    const currentIds = uniqueItems(current.map(featureIdFromValue).filter(Boolean))
    const nextPackIds = uniqueItems(packIds.map(stripFeaturePrefix).filter(Boolean))
    const added = nextPackIds.filter((id) => !currentIds.includes(id))
    const kept = currentIds.filter((id) => (mode === "replace" ? nextPackIds.includes(id) : true))
    const removed = mode === "replace" ? currentIds.filter((id) => !nextPackIds.includes(id)) : []
    const resultIds = mode === "replace" ? nextPackIds : uniqueItems([...currentIds, ...added])

    return { added, kept, removed, resultIds }
}

export function resolveFeaturePackImport({ current = [], packIds = [], features = [], mode = DEFAULT_MODE } = {}) {
    const diff = buildFeaturePackDiff({ current, packIds, mode })
    return diff.resultIds.map((id) => featureValueFromId(id, features))
}

export function buildFeaturePackExport({ name, demands = [], features = [], exportedAt } = {}) {
    const featureDemands = features.map(featureDemandFromValue).filter(Boolean)
    return {
        name,
        exportedAt: exportedAt ?? new Date().toISOString().slice(0, 10),
        demands: uniqueItems([...demands, ...featureDemands].filter(Boolean)),
    }
}

export function getFeaturePackFileName(bundleName) {
    const fallback = "auth-bundle-feature-pack"
    const slug = String(bundleName || fallback)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    return `${slug || fallback}.json`
}

function getPackItems(pack) {
    if (Array.isArray(pack)) return { items: pack, onlyFeatureDemands: false }
    if (Array.isArray(pack?.demands)) return { items: pack.demands, onlyFeatureDemands: true }
    if (Array.isArray(pack?.flags)) return { items: pack.flags, onlyFeatureDemands: false }
    if (Array.isArray(pack?.features)) return { items: pack.features, onlyFeatureDemands: false }
    return { items: [], onlyFeatureDemands: false }
}

function getPackItemId(item) {
    if (typeof item === "string") return item
    return item?.id ?? item?.demand ?? ""
}

function isFeatureId(id, onlyFeatureDemands) {
    if (!id) return false
    if (onlyFeatureDemands) return id.startsWith(FEATURE_DEMAND_PREFIX)
    return Boolean(stripFeaturePrefix(id))
}

function stripFeaturePrefix(id) {
    return String(id || "").replace(/^feature-/, "")
}

function uniqueItems(items) {
    return Array.from(new Set(items))
}
